import React, { useState, useEffect } from 'react';
import { ModalHeader, ModalBody, Input, FormText, InputGroup, InputGroupText } from 'reactstrap';
import Rodal from '../../../../components/Rodal/Rodal';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import './VDRRenameFileModal.scss';

interface VDRRenameFileModalProps {
    isVisible: boolean;
    currentName: string;
    existingNames: string[];
    isProcessing: boolean;
    serverError: string | null;
    onClose: () => void;
    onConfirm: (newName: string) => void;
}

const splitFileName = (fileName: string): { baseName: string; extension: string } => {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex <= 0) {
        return { baseName: fileName, extension: '' };
    }
    return {
        baseName: fileName.substring(0, lastDotIndex),
        extension: fileName.substring(lastDotIndex),
    };
};

const VDRRenameFileModal: React.FC<VDRRenameFileModalProps> = ({
    isVisible,
    currentName,
    existingNames,
    isProcessing,
    serverError,
    onClose,
    onConfirm,
}) => {
    const { baseName: currentBaseName, extension } = splitFileName(currentName);
    const [newBaseName, setNewBaseName] = useState<string>(currentBaseName);
    const [isServerErrorDismissed, setIsServerErrorDismissed] =
        useState<boolean>(false);

    useEffect(() => {
        if (isVisible) {
            setNewBaseName(currentBaseName);
            setIsServerErrorDismissed(false);
        }
    }, [isVisible, currentBaseName]);

    const handleClose = () => {
        setNewBaseName(currentBaseName);
        onClose();
    };

    const trimmedBaseName = newBaseName.trim();
    const newFullName = `${trimmedBaseName}${extension}`;
    const isEmptyName = trimmedBaseName.length === 0;
    const containsSlash = trimmedBaseName.includes('/');
    const isDuplicateName = existingNames.some(
        (existingName) =>
            existingName.toLowerCase() !== currentName.toLowerCase() &&
            existingName.toLowerCase() === newFullName.toLowerCase()
    );
    const clientErrorMessage = isEmptyName
        ? 'File name cannot be empty'
        : containsSlash
        ? 'File name cannot contain "/"'
        : isDuplicateName
        ? 'A file with this name already exists in this folder.'
        : null;
    const errorMessage =
        clientErrorMessage || (isServerErrorDismissed ? null : serverError);
    const isFormValid =
        !isEmptyName && !containsSlash && !isDuplicateName && trimmedBaseName !== currentBaseName;

    return (
        <div className="vdr-rename-file-modal-container">
            <Rodal
                visible={isVisible}
                onClose={handleClose}
                animation="slideTop"
                className="vdr-rename-file-modal"
                showMask={true}
                width={498}
                showCloseButton={true}>
                <ModalHeader>
                    <b>Rename File</b>
                </ModalHeader>
                <ModalBody>
                    <div className="vdr-rename-file-form">
                        <div className="form-group">
                            <label>File Name</label>
                            <InputGroup>
                                <Input
                                    type="text"
                                    value={newBaseName}
                                    onChange={(e) => {
                                        setNewBaseName(e.target.value);
                                        setIsServerErrorDismissed(true);
                                    }}
                                    placeholder="Enter file name"
                                    className={
                                        errorMessage
                                            ? 'form-control is-invalid'
                                            : 'form-control'
                                    }
                                />
                                {extension && (
                                    <InputGroupText className="file-extension-suffix">
                                        {extension}
                                    </InputGroupText>
                                )}
                            </InputGroup>
                            {errorMessage && (
                                <FormText color="danger" className="mt-1">
                                    {errorMessage}
                                </FormText>
                            )}
                        </div>

                        <div className="buttons-container">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleClose}>
                                Cancel
                            </button>
                            <ButtonLoader
                                buttonText="Rename"
                                loaderButtonText=""
                                isLoading={isProcessing}
                                disabled={!isFormValid || isProcessing}
                                onClick={() => onConfirm(newFullName)}
                                className="btn btn-primary"
                            />
                        </div>
                    </div>
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default VDRRenameFileModal;
